import { useEffect, useState, useCallback } from 'react';
import { Loader2, Sparkles, Users, RefreshCcw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import PostCard from '@/components/social/PostCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Dashboard-embedded "The Feed" — same content as /feed but rendered inside
 *  the vendor sidebar layout so vendors can scroll other members' posts
 *  without leaving their dashboard. */
export default function VendorFeedTab() {
  const { user, getAuthHeaders } = useAuth();
  const [tab, setTab] = useState('foryou');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (activeTab = tab) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/social/feed?tab=${activeTab}&limit=20`, { headers: getAuthHeaders() });
      if (res.ok) setPosts((await res.json()).posts || []);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const TabBtn = ({ id, icon: Icon, children }) => (
    <button
      onClick={() => setTab(id)}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
        tab === id
          ? 'bg-gradient-to-r from-fuchsia-500/30 to-cyan-500/30 text-white border border-fuchsia-400/50 shadow-[0_0_18px_rgba(236,72,153,0.22)]'
          : 'bg-white/5 text-zinc-300 border border-white/10 hover:bg-white/10'
      }`}
      data-testid={`vendor-feed-tab-${id}`}
    >
      <Icon size={13} /> {children}
    </button>
  );

  return (
    <div className="max-w-2xl mx-auto" data-testid="vendor-feed-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-white font-syne text-xl sm:text-2xl font-bold">The Feed</h1>
          <p className="text-zinc-400 text-xs">Live from your community.</p>
        </div>
        <button
          onClick={() => load(tab)}
          className="p-2 rounded-lg hover:bg-white/10 text-zinc-300"
          aria-label="Refresh"
          data-testid="vendor-feed-refresh"
        >
          <RefreshCcw size={14} />
        </button>
      </div>
      <div className="flex items-center gap-2 mb-5">
        <TabBtn id="following" icon={Users}>Following</TabBtn>
        <TabBtn id="foryou" icon={Sparkles}>For You</TabBtn>
      </div>

      {loading && posts.length === 0 ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-fuchsia-400" /></div>
      ) : posts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center">
          <p className="text-white font-semibold">No posts yet</p>
          <p className="text-zinc-400 text-xs mt-1">
            {tab === 'following'
              ? 'Follow some vendors and their posts will show here.'
              : "When vendors share their work it'll appear here."}
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {posts.map((p) => (
            <PostCard key={p.post_id} post={p} viewer={user} onChanged={() => load(tab)} />
          ))}
        </div>
      )}
    </div>
  );
}
